/**
 * Service for location custom datasets (Phase 2: CSV → chart).
 * CRUD for datasets and rows; CSV parse; used by Admin Settings and public pages.
 */

import { supabaseClient } from './supabase';
import { GLOBAL_ADMIN_LOCATION } from '../constants/adminSettings';

export type CustomDatasetPageId = 'transportation' | 'buildings' | 'people' | 'overview';
export type CustomDatasetChartType = 'time_series' | 'bar' | 'table';

export interface CustomDatasetRow {
  id: string;
  dataset_id: string;
  label: string;
  value: number;
  series_label: string | null;
  display_order: number;
}

export interface CustomDatasetMetric {
  id: string;
  dataset_id: string;
  label: string;
  value: string;
  display_order: number;
  color_hex: string | null;
}

export interface CustomDataset {
  id: string;
  location_name: string;
  page_id: CustomDatasetPageId;
  title: string;
  chart_type: CustomDatasetChartType;
  description: string | null;
  display_order: number;
  is_published: boolean;
  created_by: string | null;
  updated_at: string;
  overview_html: string | null;
  relevance_html: string | null;
  equity_html: string | null;
  methodology_html: string | null;
  overview_title: string | null;
  relevance_title: string | null;
  equity_title: string | null;
  methodology_title: string | null;
  x_axis_label: string | null;
  y_axis_label: string | null;
  rows?: CustomDatasetRow[];
  metrics?: CustomDatasetMetric[];
}

export interface CustomDatasetInsert {
  location_name: string;
  page_id: CustomDatasetPageId;
  title: string;
  chart_type: CustomDatasetChartType;
  description?: string | null;
  display_order?: number;
  is_published?: boolean;
  created_by?: string | null;
  overview_html?: string | null;
  relevance_html?: string | null;
  equity_html?: string | null;
  methodology_html?: string | null;
  overview_title?: string | null;
  relevance_title?: string | null;
  equity_title?: string | null;
  methodology_title?: string | null;
  x_axis_label?: string | null;
  y_axis_label?: string | null;
}

export interface CustomDatasetMetricInsert {
  dataset_id: string;
  label: string;
  value: string;
  display_order?: number;
  color_hex?: string | null;
}

export interface CustomDatasetRowInsert {
  dataset_id: string;
  label: string;
  value: number;
  series_label?: string | null;
  display_order?: number;
}

const PAGE_IDS: CustomDatasetPageId[] = ['transportation', 'buildings', 'people', 'overview'];
const CHART_TYPES: CustomDatasetChartType[] = ['time_series', 'bar', 'table'];

/** Raw metric row from location_custom_dataset_metrics (for typing Supabase response). */
interface RawMetricRow {
  id: string;
  dataset_id: string;
  label: string;
  value: string;
  display_order: number;
  color_hex: string | null;
}

/** Raw dataset row from location_custom_datasets (for typing Supabase response). */
interface RawDatasetRow {
  id: string;
  location_name: string;
  page_id: string;
  title: string;
  chart_type: string;
  description: string | null;
  display_order: number;
  is_published: boolean;
  created_by: string | null;
  updated_at: string;
  overview_html?: string | null;
  relevance_html?: string | null;
  equity_html?: string | null;
  methodology_html?: string | null;
  overview_title?: string | null;
  relevance_title?: string | null;
  equity_title?: string | null;
  methodology_title?: string | null;
  x_axis_label?: string | null;
  y_axis_label?: string | null;
}

export function getAllowedDatasetPageIds(): CustomDatasetPageId[] {
  return [...PAGE_IDS];
}

export function getChartTypes(): CustomDatasetChartType[] {
  return [...CHART_TYPES];
}

export function chartTypeToLabel(t: CustomDatasetChartType): string {
  const labels: Record<CustomDatasetChartType, string> = {
    time_series: 'Time series (line)',
    bar: 'Bar chart',
    table: 'Table'
  };
  return labels[t] ?? t;
}

export function pageIdToLabel(pageId: CustomDatasetPageId): string {
  const labels: Record<CustomDatasetPageId, string> = {
    transportation: 'Transportation',
    buildings: 'Energy & Buildings',
    people: 'People & Demographics',
    overview: 'Overview'
  };
  return labels[pageId] ?? pageId;
}

/** Header names accepted for the label/category column (e.g. x-axis or row label). */
const LABEL_HEADERS = ['label', 'year', 'category', 'x', 'date', 'period', 'name', 'id', 'group'];
/** Header names accepted for the numeric value column. */
const VALUE_HEADERS = [
  'value', 'values', 'number_of_chargers', 'count', 'total', 'y', 'amount', 'number', 'num', 'quantity', 'n',
  'percent_change', 'percent', 'change', 'pct', 'rate'
];
/** Header names accepted for the optional series column (e.g. line/bar series). */
const SERIES_HEADERS = ['series_label', 'series', 'type', 'category', 'series_name', 'legend'];

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, '_');
}

export interface ParseCsvResult {
  rows: { label: string; value: number; series_label: string | null }[];
  /** Display label for x-axis (from CSV header, e.g. "Year"). */
  x_axis_label: string;
  /** Display label for y-axis (from CSV header, e.g. "Number of Chargers"). */
  y_axis_label: string;
}

/**
 * Parse CSV text into rows plus the header names used for label/value columns.
 * Accepts flexible machine-readable headers, e.g. year, number_of_chargers, type.
 * Returns the actual header text (e.g. "Year", "Number of Chargers") for axis labels.
 */
export function parseCsvToRows(csvText: string): ParseCsvResult | null {
  const lines = csvText.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return null;

  const rawHeader = lines[0].split(',').map((c) => c.trim());
  const header = rawHeader.map((h) => normalizeHeader(h));
  const labelCol = header.findIndex((h) => LABEL_HEADERS.includes(h));
  const valueCol = header.findIndex((h) => VALUE_HEADERS.includes(h));
  const seriesCol = header.findIndex((h, idx) => SERIES_HEADERS.includes(h) && idx !== labelCol && idx !== valueCol);
  if (labelCol === -1 || valueCol === -1) return null;

  const rows: { label: string; value: number; series_label: string | null }[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map((c) => c.trim());
    const label = cells[labelCol] ?? '';
    const rawValue = cells[valueCol] ?? '';
    const num = Number(rawValue);
    if (label === '' || Number.isNaN(num)) continue;
    const series_label = seriesCol >= 0 && cells[seriesCol] !== undefined ? (cells[seriesCol] || null) : null;
    rows.push({ label, value: num, series_label });
  }
  return {
    rows,
    x_axis_label: rawHeader[labelCol] ?? 'Label',
    y_axis_label: rawHeader[valueCol] ?? 'Value'
  };
}

/**
 * Fetch published custom datasets for a location and page, with rows, ordered by display_order.
 */
export async function fetchCustomDatasets(
  locationName: string,
  pageId: CustomDatasetPageId
): Promise<CustomDataset[]> {
  const loadForLocation = async (targetLocation: string) =>
    supabaseClient
      .from('location_custom_datasets')
      .select('*')
      .eq('location_name', targetLocation)
      .eq('page_id', pageId)
      .eq('is_published', true)
      .order('display_order', { ascending: true });

  const localRes = await loadForLocation(locationName);
  if (localRes.error) {
    console.error('Error fetching custom datasets:', localRes.error);
    return [];
  }
  let datasets = localRes.data || [];
  let datasetsError: any = null;
  if (!datasets.length) {
    const globalRes = await loadForLocation(GLOBAL_ADMIN_LOCATION);
    datasets = globalRes.data || [];
    datasetsError = globalRes.error;
  }

  if (datasetsError) {
    console.error('Error fetching custom datasets:', datasetsError);
    return [];
  }
  if (!datasets?.length) return [];

  const datasetIds = datasets.map((d) => d.id);
  const rowsRes = await supabaseClient
    .from('location_custom_dataset_rows')
    .select('*')
    .in('dataset_id', datasetIds)
    .order('display_order', { ascending: true });
  let metricsRes: { data: unknown[] | null; error: unknown } = { data: null, error: null };
  try {
    metricsRes = await supabaseClient
      .from('location_custom_dataset_metrics')
      .select('*')
      .in('dataset_id', datasetIds)
      .order('display_order', { ascending: true });
  } catch {
    // Table may not exist before migration location_custom_datasets_metrics_and_narrative.sql
  }
  const rowsData = rowsRes.data;
  const metricsData = metricsRes.error ? null : metricsRes.data;
  if (rowsRes.error) console.error('Error fetching custom dataset rows:', rowsRes.error);
  if (metricsRes.error) console.error('Error fetching custom dataset metrics:', metricsRes.error);

  const rowsByDataset: Record<string, CustomDatasetRow[]> = {};
  (rowsData || []).forEach((row: { id: string; dataset_id: string; label: string; value: number; series_label: string | null; display_order: number }) => {
    const list = rowsByDataset[row.dataset_id] ?? [];
    list.push({
      id: row.id,
      dataset_id: row.dataset_id,
      label: row.label,
      value: Number(row.value),
      series_label: row.series_label ?? null,
      display_order: row.display_order ?? 0
    });
    rowsByDataset[row.dataset_id] = list;
  });
  const metricsByDataset: Record<string, CustomDatasetMetric[]> = {};
  ((metricsData || []) as RawMetricRow[]).forEach((row) => {
    const list = metricsByDataset[row.dataset_id] ?? [];
    list.push({
      id: row.id,
      dataset_id: row.dataset_id,
      label: row.label,
      value: row.value,
      display_order: row.display_order ?? 0,
      color_hex: row.color_hex ?? null
    });
    metricsByDataset[row.dataset_id] = list;
  });

  return (datasets as RawDatasetRow[]).map((d) => ({
    id: d.id,
    location_name: d.location_name,
    page_id: d.page_id as CustomDatasetPageId,
    title: d.title,
    chart_type: d.chart_type as CustomDatasetChartType,
    description: d.description ?? null,
    display_order: d.display_order ?? 0,
    is_published: !!d.is_published,
    created_by: d.created_by ?? null,
    updated_at: d.updated_at,
    overview_html: d.overview_html ?? null,
    relevance_html: d.relevance_html ?? null,
    equity_html: d.equity_html ?? null,
    methodology_html: d.methodology_html ?? null,
    overview_title: d.overview_title ?? null,
    relevance_title: d.relevance_title ?? null,
    equity_title: d.equity_title ?? null,
    methodology_title: d.methodology_title ?? null,
    x_axis_label: d.x_axis_label ?? null,
    y_axis_label: d.y_axis_label ?? null,
    rows: rowsByDataset[d.id] ?? [],
    metrics: metricsByDataset[d.id] ?? []
  }));
}

/**
 * Fetch all custom datasets for a location (all pages). For admin UI.
 */
export async function fetchAllCustomDatasetsForLocation(
  locationName: string
): Promise<CustomDataset[]> {
  const { data: datasets, error } = await supabaseClient
    .from('location_custom_datasets')
    .select('*')
    .eq('location_name', locationName)
    .order('page_id')
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching all custom datasets:', error);
    return [];
  }
  if (!datasets?.length) return [];

  const datasetIds = datasets.map((d) => d.id);
  const rowsRes = await supabaseClient
    .from('location_custom_dataset_rows')
    .select('*')
    .in('dataset_id', datasetIds)
    .order('display_order', { ascending: true });
  let metricsRes: { data: unknown[] | null; error: unknown } = { data: null, error: null };
  try {
    metricsRes = await supabaseClient
      .from('location_custom_dataset_metrics')
      .select('*')
      .in('dataset_id', datasetIds)
      .order('display_order', { ascending: true });
  } catch {
    // Table may not exist before migration
  }
  const rowsData = rowsRes.data;
  const metricsData = metricsRes.error ? null : metricsRes.data;

  const rowsByDataset: Record<string, CustomDatasetRow[]> = {};
  (rowsData || []).forEach((row: { id: string; dataset_id: string; label: string; value: number; series_label: string | null; display_order: number }) => {
    const list = rowsByDataset[row.dataset_id] ?? [];
    list.push({
      id: row.id,
      dataset_id: row.dataset_id,
      label: row.label,
      value: Number(row.value),
      series_label: row.series_label ?? null,
      display_order: row.display_order ?? 0
    });
    rowsByDataset[row.dataset_id] = list;
  });
  const metricsByDataset: Record<string, CustomDatasetMetric[]> = {};
  ((metricsData || []) as RawMetricRow[]).forEach((row) => {
    const list = metricsByDataset[row.dataset_id] ?? [];
    list.push({
      id: row.id,
      dataset_id: row.dataset_id,
      label: row.label,
      value: row.value,
      display_order: row.display_order ?? 0,
      color_hex: row.color_hex ?? null
    });
    metricsByDataset[row.dataset_id] = list;
  });

  return (datasets as RawDatasetRow[]).map((d) => ({
    id: d.id,
    location_name: d.location_name,
    page_id: d.page_id as CustomDatasetPageId,
    title: d.title,
    chart_type: d.chart_type as CustomDatasetChartType,
    description: d.description ?? null,
    display_order: d.display_order ?? 0,
    is_published: !!d.is_published,
    created_by: d.created_by ?? null,
    updated_at: d.updated_at,
    overview_html: d.overview_html ?? null,
    relevance_html: d.relevance_html ?? null,
    equity_html: d.equity_html ?? null,
    methodology_html: d.methodology_html ?? null,
    overview_title: d.overview_title ?? null,
    relevance_title: d.relevance_title ?? null,
    equity_title: d.equity_title ?? null,
    methodology_title: d.methodology_title ?? null,
    x_axis_label: d.x_axis_label ?? null,
    y_axis_label: d.y_axis_label ?? null,
    rows: rowsByDataset[d.id] ?? [],
    metrics: metricsByDataset[d.id] ?? []
  }));
}

/** Columns added by location_custom_narrative_titles_and_section_tabs.sql; omit if migration not run to avoid 400. */
const NARRATIVE_TITLE_KEYS = ['overview_title', 'relevance_title', 'equity_title', 'methodology_title'] as const;

function withoutNarrativeTitleColumns<T extends Record<string, unknown>>(payload: T): T {
  const out = { ...payload };
  NARRATIVE_TITLE_KEYS.forEach((k) => delete out[k]);
  return out as T;
}

/**
 * Create a custom dataset, its rows, and optional key metrics.
 * Omits optional narrative title columns so insert succeeds when migration has not been run (avoids 400).
 */
export async function createCustomDataset(
  payload: CustomDatasetInsert,
  rows: Omit<CustomDatasetRowInsert, 'dataset_id'>[],
  metrics: Omit<CustomDatasetMetricInsert, 'dataset_id'>[],
  userId: string
): Promise<CustomDataset | null> {
  const base = { ...payload, created_by: userId, updated_at: new Date().toISOString() };
  const insertPayload = withoutNarrativeTitleColumns(base);
  const { data: dataset, error: datasetError } = await supabaseClient
    .from('location_custom_datasets')
    .insert(insertPayload)
    .select()
    .single();

  if (datasetError || !dataset) {
    console.error('Error creating custom dataset:', datasetError);
    return null;
  }

  if (rows.length > 0) {
    const rowsPayload = rows.map((r, i) => ({
      dataset_id: dataset.id,
      label: r.label,
      value: r.value,
      series_label: r.series_label ?? null,
      display_order: r.display_order ?? i
    }));
    await supabaseClient.from('location_custom_dataset_rows').insert(rowsPayload);
  }

  if (metrics.length > 0) {
    try {
      const metricsPayload = metrics.slice(0, 4).map((m, i) => ({
        dataset_id: dataset.id,
        label: m.label,
        value: m.value,
        display_order: m.display_order ?? i,
        color_hex: m.color_hex ?? null
      }));
      await supabaseClient.from('location_custom_dataset_metrics').insert(metricsPayload);
    } catch {
      // Table may not exist before migration location_custom_datasets_metrics_and_narrative.sql
    }
  }

  return fetchDatasetById(dataset.id);
}

/**
 * Update a custom dataset, replace its rows, and replace key metrics (max 4).
 */
export async function updateCustomDataset(
  datasetId: string,
  updates: Partial<Omit<CustomDatasetInsert, 'location_name'>>,
  rows: Omit<CustomDatasetRowInsert, 'dataset_id'>[],
  metrics: Omit<CustomDatasetMetricInsert, 'dataset_id'>[],
  userId: string
): Promise<CustomDataset | null> {
  const updatePayload = withoutNarrativeTitleColumns({
    ...updates,
    updated_at: new Date().toISOString()
  });
  const { error: updateError } = await supabaseClient
    .from('location_custom_datasets')
    .update(updatePayload)
    .eq('id', datasetId);

  if (updateError) {
    console.error('Error updating custom dataset:', updateError);
    return null;
  }

  await supabaseClient.from('location_custom_dataset_rows').delete().eq('dataset_id', datasetId);
  try {
    await supabaseClient.from('location_custom_dataset_metrics').delete().eq('dataset_id', datasetId);
  } catch {
    // Table may not exist before migration
  }

  if (rows.length > 0) {
    const rowsPayload = rows.map((r, i) => ({
      dataset_id: datasetId,
      label: r.label,
      value: r.value,
      series_label: r.series_label ?? null,
      display_order: r.display_order ?? i
    }));
    await supabaseClient.from('location_custom_dataset_rows').insert(rowsPayload);
  }

  if (metrics.length > 0) {
    try {
      const metricsPayload = metrics.slice(0, 4).map((m, i) => ({
        dataset_id: datasetId,
        label: m.label,
        value: m.value,
        display_order: m.display_order ?? i,
        color_hex: m.color_hex ?? null
      }));
      await supabaseClient.from('location_custom_dataset_metrics').insert(metricsPayload);
    } catch {
      // Table may not exist before migration
    }
  }

  return fetchDatasetById(datasetId);
}

/**
 * Delete a custom dataset (cascade deletes rows).
 */
export async function deleteCustomDataset(datasetId: string): Promise<boolean> {
  const { error } = await supabaseClient
    .from('location_custom_datasets')
    .delete()
    .eq('id', datasetId);

  if (error) {
    console.error('Error deleting custom dataset:', error);
    return false;
  }
  return true;
}

async function fetchDatasetById(datasetId: string): Promise<CustomDataset | null> {
  const { data: dataset, error } = await supabaseClient
    .from('location_custom_datasets')
    .select('*')
    .eq('id', datasetId)
    .single();

  if (error || !dataset) return null;

  const rowsRes = await supabaseClient
    .from('location_custom_dataset_rows')
    .select('*')
    .eq('dataset_id', datasetId)
    .order('display_order', { ascending: true });
  let metricsRes: { data: unknown[] | null; error: unknown } = { data: null, error: null };
  try {
    metricsRes = await supabaseClient
      .from('location_custom_dataset_metrics')
      .select('*')
      .eq('dataset_id', datasetId)
      .order('display_order', { ascending: true });
  } catch {
    // Table may not exist before migration
  }
  const rowsData = rowsRes.data;
  const metricsData = metricsRes.error ? null : metricsRes.data;

  const rows: CustomDatasetRow[] = (rowsData || []).map((row: { id: string; dataset_id: string; label: string; value: number; series_label: string | null; display_order: number }) => ({
    id: row.id,
    dataset_id: row.dataset_id,
    label: row.label,
    value: Number(row.value),
    series_label: row.series_label ?? null,
    display_order: row.display_order ?? 0
  }));
  const metrics: CustomDatasetMetric[] = ((metricsData || []) as RawMetricRow[]).map((row) => ({
    id: row.id,
    dataset_id: row.dataset_id,
    label: row.label,
    value: row.value,
    display_order: row.display_order ?? 0,
    color_hex: row.color_hex ?? null
  }));

  const d = dataset as RawDatasetRow;
  return {
    id: d.id,
    location_name: d.location_name,
    page_id: d.page_id as CustomDatasetPageId,
    title: d.title,
    chart_type: d.chart_type as CustomDatasetChartType,
    description: d.description ?? null,
    display_order: d.display_order ?? 0,
    is_published: !!d.is_published,
    created_by: d.created_by ?? null,
    updated_at: d.updated_at,
    overview_html: d.overview_html ?? null,
    relevance_html: d.relevance_html ?? null,
    equity_html: d.equity_html ?? null,
    methodology_html: d.methodology_html ?? null,
    overview_title: d.overview_title ?? null,
    relevance_title: d.relevance_title ?? null,
    equity_title: d.equity_title ?? null,
    methodology_title: d.methodology_title ?? null,
    x_axis_label: d.x_axis_label ?? null,
    y_axis_label: d.y_axis_label ?? null,
    rows,
    metrics
  };
}
